/**
 * CENTRALIZED SERVER STRING REGISTRY
 *
 * CRITICAL: All user-facing strings returned from server MUST be defined here.
 * This includes: errors, success messages, email content, notifications, etc.
 *
 * Each string includes:
 * - key: camelCase identifier (matches i18n key)
 * - namespace: i18n namespace (common, lists, premium, etc.)
 * - type: String category (error, success, email, etc.)
 * - httpStatus: (optional) HTTP status code for API errors
 * - devNote: (optional) Context for developers
 */

export const SERVER_STRINGS = {
  // ============================================================================
  // API ERRORS
  // ============================================================================

  // Auth Errors (namespace: common)
  UNAUTHORIZED: {
    key: "unauthorized",
    namespace: "common",
    type: "error",
    httpStatus: 401,
  },
  FORBIDDEN: {
    key: "forbidden",
    namespace: "common",
    type: "error",
    httpStatus: 403,
  },
  USER_NOT_FOUND: {
    key: "userNotFound",
    namespace: "common",
    type: "error",
    httpStatus: 404,
  },
  INVALID_TOKEN: {
    key: "invalidToken",
    namespace: "common",
    type: "error",
    httpStatus: 400,
  },

  // Recipe Errors (namespace: recipes)
  RECIPE_NOT_FOUND: {
    key: "recipeNotFound",
    namespace: "recipes",
    type: "error",
    httpStatus: 404,
  },
  RECIPE_FORBIDDEN: {
    key: "recipeForbidden",
    namespace: "recipes",
    type: "error",
    httpStatus: 403,
  },
  RECIPE_PUBLISH_REQUIRES_IMAGE: {
    key: "recipePublishRequiresImage",
    namespace: "recipes",
    type: "error",
    httpStatus: 422,
  },
  METHOD_NOT_ALLOWED: {
    key: "methodNotAllowed",
    namespace: "common",
    type: "error",
    httpStatus: 405,
  },
  INVALID_BODY: {
    key: "invalidBody",
    namespace: "common",
    type: "error",
    httpStatus: 400,
  },

  // Invitation Errors (namespace: lists)
  INVITATION_TOKEN_REQUIRED: {
    key: "invitationTokenRequired",
    namespace: "lists",
    type: "error",
    httpStatus: 400,
  },
  INVITATION_EXPIRED: {
    key: "invitationExpired",
    namespace: "lists",
    type: "error",
    httpStatus: 410,
    devNote: "7 days after creation",
  },
  INVITATION_ALREADY_ACCEPTED: {
    key: "invitationAlreadyAccepted",
    namespace: "lists",
    type: "error",
    httpStatus: 400,
  },
  INVALID_OR_EXPIRED_INVITATION: {
    key: "invalidOrExpiredInvitation",
    namespace: "lists",
    type: "error",
    httpStatus: 404,
  },
  CANNOT_ACCEPT_OWN_INVITATION: {
    key: "cannotAcceptOwnInvitation",
    namespace: "lists",
    type: "error",
    httpStatus: 400,
  },

  // List Sharing Errors (namespace: lists)
  LIST_NOT_FOUND: {
    key: "listNotFound",
    namespace: "lists",
    type: "error",
    httpStatus: 404,
  },
  CANNOT_SHARE_WITH_SELF: {
    key: "cannotShareWithSelf",
    namespace: "lists",
    type: "error",
    httpStatus: 400,
  },
  LIST_ALREADY_SHARED: {
    key: "listAlreadyShared",
    namespace: "lists",
    type: "error",
    httpStatus: 400,
  },
  INVITATION_ID_REQUIRED: {
    key: "invitationIdRequired",
    namespace: "lists",
    type: "error",
    httpStatus: 400,
  },
  INVITATION_NOT_FOUND: {
    key: "invitationNotFound",
    namespace: "lists",
    type: "error",
    httpStatus: 404,
  },
  INVALID_INVITATION: {
    key: "invalidInvitation",
    namespace: "lists",
    type: "error",
    httpStatus: 400,
  },
  CANNOT_RESEND_ACCEPTED_INVITATION: {
    key: "cannotResendAcceptedInvitation",
    namespace: "lists",
    type: "error",
    httpStatus: 400,
  },
  SHARE_INVITATION_NOT_FOUND: {
    key: "shareInvitationNotFound",
    namespace: "lists",
    type: "error",
    httpStatus: 404,
  },
  SHARE_NOT_FOUND: {
    key: "shareNotFound",
    namespace: "lists",
    type: "error",
    httpStatus: 404,
  },
  CANNOT_LEAVE_OWN_LIST: {
    key: "cannotLeaveOwnList",
    namespace: "lists",
    type: "error",
    httpStatus: 400,
  },
  FAILED_TO_SEND_EMAIL: {
    key: "failedToSendEmail",
    namespace: "lists",
    type: "error",
    httpStatus: 500,
  },
  FAILED_TO_RESEND_INVITATION: {
    key: "failedToResendInvitation",
    namespace: "lists",
    type: "error",
    httpStatus: 500,
  },
  FAILED_TO_REMOVE_SHARE: {
    key: "failedToRemoveShare",
    namespace: "lists",
    type: "error",
    httpStatus: 500,
  },
  FAILED_TO_FETCH_SHARES: {
    key: "failedToFetchShares",
    namespace: "lists",
    type: "error",
    httpStatus: 500,
  },
  FAILED_TO_LEAVE_LIST: {
    key: "failedToLeaveList",
    namespace: "lists",
    type: "error",
    httpStatus: 500,
  },

  // Calendar Sharing Errors (namespace: calendar)
  CALENDAR_ALREADY_SHARED: {
    key: "calendarAlreadyShared",
    namespace: "calendar",
    type: "error",
    httpStatus: 400,
  },
  CANNOT_SHARE_CALENDAR_WITH_SELF: {
    key: "cannotShareCalendarWithSelf",
    namespace: "calendar",
    type: "error",
    httpStatus: 400,
  },
  CALENDAR_SHARE_NOT_FOUND: {
    key: "calendarShareNotFound",
    namespace: "calendar",
    type: "error",
    httpStatus: 404,
  },
  CANNOT_LEAVE_OWN_CALENDAR: {
    key: "cannotLeaveOwnCalendar",
    namespace: "calendar",
    type: "error",
    httpStatus: 400,
  },
  FAILED_TO_SHARE_CALENDAR: {
    key: "failedToShareCalendar",
    namespace: "calendar",
    type: "error",
    httpStatus: 500,
  },
  FAILED_TO_FETCH_CALENDAR_SHARES: {
    key: "failedToFetchCalendarShares",
    namespace: "calendar",
    type: "error",
    httpStatus: 500,
  },
  FAILED_TO_REMOVE_CALENDAR_SHARE: {
    key: "failedToRemoveCalendarShare",
    namespace: "calendar",
    type: "error",
    httpStatus: 500,
  },
  FAILED_TO_RESEND_CALENDAR_INVITATION: {
    key: "failedToResendCalendarInvitation",
    namespace: "calendar",
    type: "error",
    httpStatus: 500,
  },
  FAILED_TO_LEAVE_CALENDAR: {
    key: "failedToLeaveCalendar",
    namespace: "calendar",
    type: "error",
    httpStatus: 500,
  },

  // Subscription Errors (namespace: premium)
  NO_ACTIVE_SUBSCRIPTION: {
    key: "noActiveSubscription",
    namespace: "premium",
    type: "error",
    httpStatus: 400,
  },
  INVALID_INTERVAL: {
    key: "invalidInterval",
    namespace: "premium",
    type: "error",
    httpStatus: 400,
  },

  // Referral Errors (namespace: referral)
  REFERRAL_LIMIT_REACHED: {
    key: "referralLimitReached",
    namespace: "referral",
    type: "error",
    httpStatus: 400,
    devNote: "Max 3 referrals per user",
  },
  CANNOT_REFER_YOURSELF: {
    key: "cannotReferYourself",
    namespace: "referral",
    type: "error",
    httpStatus: 400,
  },
  CANNOT_SEND_MUTUAL_INVITATIONS: {
    key: "cannotSendMutualInvitations",
    namespace: "referral",
    type: "error",
    httpStatus: 400,
  },
  ALREADY_SENT_REFERRAL: {
    key: "alreadySentReferral",
    namespace: "referral",
    type: "error",
    httpStatus: 400,
  },
  REFERRAL_ID_REQUIRED: {
    key: "referralIdRequired",
    namespace: "referral",
    type: "error",
    httpStatus: 400,
  },
  REFERRAL_NOT_FOUND: {
    key: "referralNotFound",
    namespace: "referral",
    type: "error",
    httpStatus: 404,
  },
  CANNOT_DELETE_ACCEPTED_REFERRAL: {
    key: "cannotDeleteAcceptedReferral",
    namespace: "referral",
    type: "error",
    httpStatus: 400,
  },
  REFERRAL_TOKEN_REQUIRED: {
    key: "referralTokenRequired",
    namespace: "referral",
    type: "error",
    httpStatus: 400,
  },
  INVALID_REFERRAL_TOKEN: {
    key: "invalidReferralToken",
    namespace: "referral",
    type: "error",
    httpStatus: 404,
  },
  REFERRAL_ALREADY_ACCEPTED: {
    key: "referralAlreadyAccepted",
    namespace: "referral",
    type: "error",
    httpStatus: 400,
  },
  REFERRAL_FOR_NEW_ACCOUNTS: {
    key: "referralForNewAccounts",
    namespace: "referral",
    type: "error",
    httpStatus: 400,
    devNote: "Only an account made after the invitation was sent can accept it",
  },
  REFERRAL_ALREADY_USED: {
    key: "referralAlreadyUsed",
    namespace: "referral",
    type: "error",
    httpStatus: 400,
    devNote: "Each account accepts at most one referral",
  },
  FAILED_TO_ACCEPT_REFERRAL: {
    key: "failedToAcceptReferral",
    namespace: "referral",
    type: "error",
    httpStatus: 500,
  },
  FAILED_TO_FETCH_REFERRAL: {
    key: "failedToFetchReferral",
    namespace: "referral",
    type: "error",
    httpStatus: 500,
  },
  FAILED_TO_FETCH_REFERRALS: {
    key: "failedToFetchReferrals",
    namespace: "referral",
    type: "error",
    httpStatus: 500,
  },
  FAILED_TO_DELETE_REFERRAL: {
    key: "failedToDeleteReferral",
    namespace: "referral",
    type: "error",
    httpStatus: 500,
  },
  FAILED_TO_RESEND_REFERRAL: {
    key: "failedToResendReferral",
    namespace: "referral",
    type: "error",
    httpStatus: 500,
  },

  // Sync Errors (namespace: sync)
  SYNC_OPERATIONS_FAILED: {
    key: "syncOperationsFailed",
    namespace: "sync",
    type: "error",
    httpStatus: 500,
  },

  // Import URL Errors (namespace: ingredients)
  URL_REQUIRED: {
    key: "urlRequired",
    namespace: "ingredients",
    type: "error",
    httpStatus: 400,
  },
  INVALID_URL: {
    key: "invalidUrl",
    namespace: "ingredients",
    type: "error",
    httpStatus: 400,
  },
  FAILED_TO_FETCH_URL: {
    key: "failedToFetchUrl",
    namespace: "ingredients",
    type: "error",
    httpStatus: 400,
    devNote: "Could not fetch the recipe URL",
  },
  FAILED_TO_EXTRACT_INGREDIENTS: {
    key: "failedToExtractIngredients",
    namespace: "ingredients",
    type: "error",
    httpStatus: 500,
    devNote: "AI extraction failed",
  },
  LLM_NOT_CONFIGURED: {
    key: "aiNotConfigured",
    namespace: "ingredients",
    type: "error",
    httpStatus: 503,
    devNote: "No LLM API key configured (GROQ_API_KEY or GEMINI_API_KEY)",
  },

  // Prep Ahead Errors (namespace: dashboard)
  PREP_AHEAD_LIMIT_REACHED: {
    key: "prepAheadLimitReached",
    namespace: "dashboard",
    type: "error",
    httpStatus: 429,
  },
  PREP_AHEAD_NO_RECIPES: {
    key: "prepAheadNoRecipes",
    namespace: "dashboard",
    type: "error",
    httpStatus: 400,
  },
  PREP_AHEAD_FAILED: {
    key: "prepAheadFailed",
    namespace: "dashboard",
    type: "error",
    httpStatus: 500,
  },

  // Meal Suggestion Errors (namespace: calendar)
  MEAL_SUGGEST_NO_RECIPES: {
    key: "mealSuggestNoRecipes",
    namespace: "calendar",
    type: "error",
    httpStatus: 400,
  },
  MEAL_SUGGEST_WEEK_IN_PAST: {
    key: "mealSuggestWeekInPast",
    namespace: "calendar",
    type: "error",
    httpStatus: 400,
  },
  MEAL_PLAN_DRAFT_GONE: {
    key: "mealPlanDraftGone",
    namespace: "dashboard",
    type: "error",
    httpStatus: 404,
  },
  MEAL_SUGGEST_FAILED: {
    key: "mealSuggestFailed",
    namespace: "calendar",
    type: "error",
    httpStatus: 500,
  },

  // AI Usage Errors (namespace: common)
  AI_USAGE_LIMIT_REACHED: {
    key: "aiUsageLimitReached",
    namespace: "common",
    type: "error",
    httpStatus: 429,
    devNote: "Weekly AI usage limit exceeded (shared pool across all AI features)",
  },
  RECIPE_CHAT_FAILED: {
    key: "recipeChatFailed",
    namespace: "recipes",
    type: "error",
    httpStatus: 500,
  },

  // Analytics Errors (namespace: common)
  ANALYTICS_UNAVAILABLE: {
    key: "analyticsUnavailable",
    namespace: "common",
    type: "error",
    httpStatus: 503,
  },

  // Limit Errors (namespace: common)
  LIMIT_EXCEEDED: {
    key: "limitExceeded",
    namespace: "common",
    type: "error",
    httpStatus: 400,
  },

  // Agents (namespace: agents)
  AGENTS_CANNOT_MANAGE_KEYS: {
    key: "agentsCannotManageKeys",
    namespace: "agents",
    type: "error",
    httpStatus: 403,
    devNote: "Agent keys are managed by the person in Settings, not with an agent token",
  },
  AGENT_KEY_NAME_INVALID: {
    key: "agentKeyNameInvalid",
    namespace: "agents",
    type: "error",
    httpStatus: 400,
  },
  AGENT_KEY_LIMIT: {
    key: "agentKeyLimit",
    namespace: "agents",
    type: "error",
    httpStatus: 400,
    devNote: "MAX_ACTIVE_AGENT_TOKENS in lib/agents/tokens.ts",
  },
  AGENT_KEY_NOT_FOUND: {
    key: "agentKeyNotFound",
    namespace: "agents",
    type: "error",
    httpStatus: 404,
  },
  AGENT_CODE_NOT_FOUND: {
    key: "agentCodeNotFound",
    namespace: "agents",
    type: "error",
    httpStatus: 404,
    devNote: "The approve page (/agents/approve): no waiting request has this short code",
  },
  AGENT_CODE_EXPIRED: {
    key: "agentCodeExpired",
    namespace: "agents",
    type: "error",
    httpStatus: 410,
    devNote: "The approve page: the request ran out of time or was already allowed or denied",
  },
  AGENT_USE_DELETE_TOOL: {
    key: "agentUseDeleteTool",
    namespace: "agents",
    type: "error",
    httpStatus: 403,
    devNote:
      "An agent token can't call DELETE /api/account/delete; agents use the account_delete tool, which asks for confirmation",
  },
  AGENT_CANNOT_APPROVE: {
    key: "agentCannotApprove",
    namespace: "agents",
    type: "error",
    httpStatus: 403,
    devNote: "Only a person approves an agent's connection; an agent token never does",
  },
  OAUTH_CONSENT_EXPIRED: {
    key: "oauthConsentExpired",
    namespace: "agents",
    type: "error",
    httpStatus: 410,
    devNote:
      "The OAuth consent page (/oauth/authorize) was open too long, or for another person; answered 200 { error }",
  },
  OAUTH_REQUEST_INVALID: {
    key: "oauthRequestInvalid",
    namespace: "agents",
    type: "error",
    httpStatus: 400,
    devNote:
      "The OAuth consent page's request no longer checks out (client or redirect changed); answered 200 { error }",
  },

  // Rate Limiting (namespace: common)
  RATE_LIMIT_EXCEEDED: {
    key: "rateLimitExceeded",
    namespace: "common",
    type: "error",
    httpStatus: 429,
  },

  // Profile Errors (namespace: settings)
  USERNAME_TAKEN: {
    key: "usernameTaken",
    namespace: "settings",
    type: "error",
    httpStatus: 409,
  },
  USERNAME_INVALID: {
    key: "usernameInvalid",
    namespace: "settings",
    type: "error",
    httpStatus: 400,
  },

  // Generic Server Errors (namespace: common)
  INTERNAL_SERVER_ERROR: {
    key: "internalServerError",
    namespace: "common",
    type: "error",
    httpStatus: 500,
  },
  CONFIGURATION_ERROR: {
    key: "configurationError",
    namespace: "common",
    type: "error",
    httpStatus: 500,
  },

  // Feature-Specific Failures (5xx)
  FAILED_TO_ACCEPT_INVITATION: {
    key: "failedToAcceptInvitation",
    namespace: "lists",
    type: "error",
    httpStatus: 500,
  },
  FAILED_TO_SHARE_LIST: {
    key: "failedToShare",
    namespace: "lists",
    type: "error",
    httpStatus: 500,
  },
  FAILED_TO_CREATE_REFERRAL: {
    key: "failedToCreateReferral",
    namespace: "referral",
    type: "error",
    httpStatus: 500,
  },

  // ============================================================================
  // SUCCESS MESSAGES
  // ============================================================================

  INVITATION_SENT: {
    key: "invitationSent",
    namespace: "lists",
    type: "success",
    devNote: "Shown after sharing list with email",
  },
  INVITATION_RESENT: {
    key: "invitationResent",
    namespace: "lists",
    type: "success",
  },
  SHARE_REMOVED: {
    key: "shareRemoved",
    namespace: "lists",
    type: "success",
  },
  INGREDIENTS_IMPORTED: {
    key: "ingredientsImported",
    namespace: "ingredients",
    type: "success",
    devNote: "Shown after successfully importing ingredients from URL",
  },
  SUBSCRIPTION_CANCELLED: {
    key: "subscriptionCancelled",
    namespace: "premium",
    type: "success",
  },
  SUBSCRIPTION_REACTIVATED: {
    key: "subscriptionReactivated",
    namespace: "premium",
    type: "success",
  },
  SUBSCRIPTION_ACTIVATED: {
    key: "subscriptionActivated",
    namespace: "premium",
    type: "success",
    devNote: "Shown after Apple subscription verification succeeds",
  },
  REFERRAL_SENT: {
    key: "referralSent",
    namespace: "referral",
    type: "success",
  },
  ACCOUNT_DELETED: {
    key: "accountDeleted",
    namespace: "settings",
    type: "success",
    devNote: "Shown after user successfully deletes their account",
  },
  PROFILE_UPDATED: {
    key: "profileUpdated",
    namespace: "settings",
    type: "success",
    devNote: "Shown after user updates their profile/username",
  },

  // ============================================================================
  // EMAIL SUBJECTS (namespace determines which locale file)
  // ============================================================================

  EMAIL_SUBJECT_LIST_INVITATION: {
    key: "emailSubjectListInvitation",
    namespace: "email",
    type: "email_subject",
    devNote: "Subject for list sharing invitation",
    // i18n: "{ownerName} invited you to \"{listName}\""
  },
  EMAIL_SUBJECT_REFERRAL: {
    key: "emailSubjectReferral",
    namespace: "email",
    type: "email_subject",
    devNote: "Subject for referral invitation",
    // i18n: "{referrerName} invited you to try Nombox - Get 1 Month Free Premium!"
  },

  // ============================================================================
  // EMAIL CONTENT (namespace: email)
  // ============================================================================

  // List Invitation Email
  EMAIL_LIST_INVITATION_TITLE: {
    key: "emailListInvitationTitle",
    namespace: "email",
    type: "email_content",
    // i18n: "List Sharing Invitation"
  },
  EMAIL_LIST_INVITATION_GREETING: {
    key: "emailListInvitationGreeting",
    namespace: "email",
    type: "email_content",
    // i18n: "Hi there!"
  },
  EMAIL_LIST_INVITATION_BODY: {
    key: "emailListInvitationBody",
    namespace: "email",
    type: "email_content",
    devNote: "Main message body with owner name",
    // i18n: "{ownerName} has invited you to collaborate on their shopping list:"
  },
  EMAIL_LIST_INVITATION_CTA: {
    key: "emailListInvitationCta",
    namespace: "email",
    type: "email_content",
    // i18n: "Accept Invitation"
  },
  EMAIL_LIST_INVITATION_DESCRIPTION: {
    key: "emailListInvitationDescription",
    namespace: "email",
    type: "email_content",
    // i18n: "This shared list will appear in your Nombox app once you accept..."
  },
  EMAIL_LIST_INVITATION_FOOTER: {
    key: "emailListInvitationFooter",
    namespace: "email",
    type: "email_content",
    // i18n: "If you didn't expect this invitation, you can safely ignore this email."
  },

  // Referral Email
  EMAIL_REFERRAL_TITLE: {
    key: "emailReferralTitle",
    namespace: "email",
    type: "email_content",
    // i18n: "You're Invited to Nombox!"
  },
  EMAIL_REFERRAL_GREETING: {
    key: "emailReferralGreeting",
    namespace: "email",
    type: "email_content",
    // i18n: "Hi there!"
  },
  EMAIL_REFERRAL_BODY: {
    key: "emailReferralBody",
    namespace: "email",
    type: "email_content",
    // i18n: "{referrerName} thinks you'll love Nombox - the smart grocery shopping app..."
  },
  EMAIL_REFERRAL_BONUS: {
    key: "emailReferralBonus",
    namespace: "email",
    type: "email_content",
    // i18n: "Get 1 month of Premium free!"
  },
  EMAIL_REFERRAL_CTA: {
    key: "emailReferralCta",
    namespace: "email",
    type: "email_content",
    // i18n: "Claim Your Free Month"
  },

  // Common Email Elements
  EMAIL_BRAND_TAGLINE: {
    key: "emailBrandTagline",
    namespace: "email",
    type: "email_content",
    // i18n: "Your smart grocery shopping companion"
  },
  EMAIL_LINK_FALLBACK: {
    key: "emailLinkFallback",
    namespace: "email",
    type: "email_content",
    // i18n: "Can't see the button? Copy this link:"
  },

  // ============================================================================
  // SYSTEM NOTIFICATIONS (namespace varies)
  // ============================================================================

  REFERRAL_BONUS_DESCRIPTION: {
    key: "referralBonusDescription",
    namespace: "referral",
    type: "notification",
    // i18n: "Referral bonus - 1 month free"
  },

  // ============================================================================
  // LIMIT WARNINGS (namespace: premium or sync)
  // ============================================================================

  FREE_TIER_INGREDIENT_LIMIT_WARNING: {
    key: "freeTierIngredientLimitWarning",
    namespace: "sync",
    type: "warning",
    devNote: "Shown during sync when approaching limit",
    // i18n: "Free tier limit: {limit} ingredients per month. {remaining} remaining."
  },
  FREE_TIER_LIST_LIMIT_WARNING: {
    key: "freeTierListLimitWarning",
    namespace: "sync",
    type: "warning",
    devNote: "Shown during sync when at list limit",
    // i18n: "Free tier limit: {limit} lists. Upgrade to premium for unlimited lists."
  },
} as const;

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type ServerStringKey = keyof typeof SERVER_STRINGS;
